import { useState, useEffect, useRef } from 'react';
import { Bell, Check, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../lib/api';
import { toast } from 'sonner';

const POLL_INTERVAL = 30000; // 30s

const NotificationBell = ({ variant = 'light' }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const prevUnreadRef = useRef(0);
  const navigate = useNavigate();

  const colorClasses = variant === 'dark'
    ? 'text-white hover:bg-slate-800'
    : 'text-slate-700 hover:bg-slate-100';

  const load = async () => {
    try {
      const { data } = await api.get('/admin/notifications?limit=30');
      setItems(data.items || []);
      const newUnread = data.unread_count || 0;
      // Toast quando uma nova notificação chega
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        const diff = newUnread - prevUnreadRef.current;
        if (prevUnreadRef.current > 0 || newUnread > 0) {
          // Evita toast no primeiro load
          if (prevUnreadRef.current > 0) {
            toast.info(`Você tem ${diff} nova(s) notificação(ões)!`);
          }
        }
      }
      prevUnreadRef.current = newUnread;
      setUnread(newUnread);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(t);
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/admin/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      toast.error('Erro ao marcar como lida');
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.put('/admin/notifications/read-all');
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success('Todas marcadas como lidas');
    } catch (e) {
      toast.error('Erro');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.order_id) {
      setOpen(false);
      navigate('/admin/pedidos');
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${colorClasses}`}
          data-testid="admin-notifications-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="admin-notifications-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] max-h-[500px] overflow-hidden p-0"
        data-testid="admin-notifications-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Notificações</h3>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              disabled={loading}
              className="text-xs text-indigo-600 hover:underline"
              data-testid="mark-all-read"
            >
              Marcar todas como lidas
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Sem notificações por aqui
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                className={`px-4 py-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                  !n.read ? 'bg-indigo-50/50' : ''
                }`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && (
                    <span className="w-2 h-2 bg-indigo-600 rounded-full mt-1.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{n.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-line line-clamp-3">
                      {n.message}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] text-slate-400">
                        {new Date(n.created_at).toLocaleString('pt-BR')}
                      </span>
                      {n.meta?.customer_whatsapp_link && (
                        <a
                          href={n.meta.customer_whatsapp_link}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] text-emerald-600 hover:underline flex items-center gap-0.5"
                        >
                          WhatsApp <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
